import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './PollBubble.module.css';
import { getPollDetails, votePoll, closePoll } from '../../../../apis/chatApi';

const LONG_PRESS_MS = 550;

const PollBubble = ({ pollId, onRegisterNotice, className, currentUserId, currentUserName, fallbackCreatorId, fallbackCreatorName, compactWhenClosed = false }) => {
    const [poll, setPoll] = useState(null);
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showActions, setShowActions] = useState(false);
    const pressTimerRef = useRef(null);
    const [closing, setClosing] = useState(false);

    const clearPressTimer = () => {
        if (pressTimerRef.current) {
            clearTimeout(pressTimerRef.current);
            pressTimerRef.current = null;
        }
    };

    const loadPoll = useCallback(async () => {
        try {
            const data = await getPollDetails(pollId);
            setPoll(data);
        } catch (e) {
            console.error("Poll load failed", e);
        } finally {
            setLoading(false);
        }
    }, [pollId]);

    useEffect(() => {
        loadPoll();
    }, [loadPoll]);

    const handleVote = async () => {
        if (!selectedOptionId) return;
        try {
            await votePoll(pollId, selectedOptionId);
            // 투표 후 데이터 갱신
            loadPoll();
        } catch (e) {
            console.error(e);
            alert('투표 처리에 실패했습니다. 이미 투표했거나 오류가 발생했습니다.');
        }
    };

    const handleCloseActions = useCallback(() => {
        setShowActions(false);
    }, []);

    const handleRegister = async () => {
        if (!onRegisterNotice) return;
        setShowActions(false);
        await onRegisterNotice({ pollId, poll });
    };

    const onPointerDown = () => {
        if (!onRegisterNotice) return;
        clearPressTimer();
        pressTimerRef.current = setTimeout(() => {
            setShowActions(true);
        }, LONG_PRESS_MS);
    };

    const onPointerUp = () => {
        clearPressTimer();
    };

    const onPointerCancel = () => {
        clearPressTimer();
    };

    const onContextMenu = (e) => {
        if (!onRegisterNotice) return;
        e.preventDefault();
        setShowActions(true);
    };

    useEffect(() => {
        return () => {
            clearPressTimer();
        };
    }, []);

    const handleClosePoll = async () => {
        setClosing(true);
        try {
            await closePoll(pollId);
            await loadPoll();
            alert('투표가 종료되었습니다.');
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.message || '투표 종료에 실패했습니다. 권한을 확인해주세요.';
            alert(msg);
        } finally {
            setClosing(false);
        }
    };

    if (loading) return <div className={`${styles.container} ${className ?? ''}`}>투표 불러오는 중...</div>;
    if (!poll) return <div className={`${styles.container} ${className ?? ''}`}>투표 정보를 찾을 수 없습니다.</div>;

    const totalVotes = poll.options.reduce((acc, o) => acc + (o.voteCount || 0), 0);
    const creatorIdCandidates = [
        poll?.creatorId,
        poll?.creatorUserId,
        poll?.ownerId,
        poll?.createdBy,
        poll?.userId,
        poll?.creator?.id,
        poll?.creator?.userId,
        poll?.user?.id,
        poll?.authorId,
        fallbackCreatorId
    ].filter((v) => v !== undefined && v !== null);
    const creatorNameCandidates = [
        poll?.creatorName,
        poll?.createdByName,
        poll?.ownerName,
        poll?.userName,
        poll?.creator?.name,
        poll?.creator?.username,
        poll?.user?.name,
        fallbackCreatorName
    ].filter(Boolean);
    const isCreatorById = !!currentUserId && creatorIdCandidates.some((id) => String(id) === String(currentUserId));
    const normalizedMeName = currentUserName ? String(currentUserName).trim().toLowerCase() : null;
    const isCreatorByName = !!normalizedMeName && creatorNameCandidates.some((n) => String(n).trim().toLowerCase() === normalizedMeName);
    const isCreator = isCreatorById || isCreatorByName;
    const isClosed = !!(poll?.closed || poll?.isClosed || poll?.closedAt || (poll?.status && String(poll.status).toUpperCase() === 'CLOSED'));
    const isVoted = !!poll.myVotedOptionId;
    const showResult = isClosed || isVoted;
    const sortedOptions = [...poll.options].sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    const hasAnyVote = totalVotes > 0;

    if (showResult && compactWhenClosed && isClosed) {
        const top3 = sortedOptions.slice(0, 3);
        return (
            <div className={`${styles.container} ${styles.compactContainer} ${className ?? ''}`}>
                <div className={styles.compactHeader}>
                    <div className={styles.compactTitle}>{poll.question}</div>
                    <span className={styles.closedBadge}>투표 마감</span>
                </div>
                <div className={styles.compactMeta}>{totalVotes}명 참여</div>
                <div className={styles.compactOptionList}>
                    {top3.map((opt, idx) => {
                        const percent = totalVotes === 0 ? 0 : Math.round((opt.voteCount / totalVotes) * 100);
                        return (
                            <div key={opt.optionId ?? idx} className={styles.compactOptionRow}>
                                <span className={styles.compactOptionText}>{opt.text}</span>
                                <span className={styles.compactOptionMeta}>{opt.voteCount ?? 0}표 ({percent}%)</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (showResult) {
        // 결과 화면
        return (
            <div
                className={`${styles.container} ${className ?? ''} ${onRegisterNotice ? styles.actionHost : ''}`}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onContextMenu={onContextMenu}
            >
                {showActions && (
                    <div className={styles.actionMenu} role="menu">
                        <button type="button" className={styles.actionItem} onClick={handleRegister}>
                            공지로 등록하기
                        </button>
                        <button type="button" className={styles.actionItemSecondary} onClick={handleCloseActions}>
                            취소
                        </button>
                    </div>
                )}
                <div className={styles.header}>
                    <div className={styles.titleRow}>
                        <span className={styles.qMark}>Q</span>
                        <span className={styles.title}>{poll.question}</span>
                    </div>
                    <span className={styles.subtitle}>{totalVotes}명 참여 • {isClosed ? '마감' : '종료'}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {poll.options.map(opt => {
                        const percent = totalVotes === 0 ? 0 : Math.round((opt.voteCount / totalVotes) * 100);
                        const isMyPick = opt.optionId === poll.myVotedOptionId;
                        return (
                            <div key={opt.optionId} className={styles.resultItem}>
                                <div className={styles.resultTextRow}>
                                    <span style={{ fontWeight: isMyPick ? 'bold' : 'normal', color: isMyPick ? '#FF5C00' : '#333' }}>
                                        {opt.text} {isMyPick && "✓"}
                                    </span>
                                    <span>{opt.voteCount}표 ({percent}%)</span>
                                </div>
                                <div className={styles.resultBarBg}>
                                    <div
                                        className={styles.resultBarFill}
                                        style={{ width: `${percent}%`, opacity: isMyPick ? 1 : 0.6 }}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div className={styles.footerRow}>
                    <span className={styles.closedBadge}>{isClosed ? '투표 마감' : '투표 완료'}</span>
                    {isCreator && !isClosed && hasAnyVote && (
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={handleClosePoll}
                            disabled={closing}
                        >
                            {closing ? '종료 중...' : '투표 종료'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // 투표 대기 화면
    return (
        <div
            className={`${styles.container} ${className ?? ''} ${onRegisterNotice ? styles.actionHost : ''}`}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onContextMenu={onContextMenu}
        >
            {showActions && (
                <div className={styles.actionMenu} role="menu">
                    <button type="button" className={styles.actionItem} onClick={handleRegister}>
                        공지로 등록하기
                    </button>
                    <button type="button" className={styles.actionItemSecondary} onClick={handleCloseActions}>
                        취소
                    </button>
                </div>
            )}
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <span className={styles.qMark}>Q</span>
                    <span className={styles.title}>{poll.question}</span>
                </div>
                <span className={styles.subtitle}>{totalVotes}명 참여 • 미참여</span>
            </div>

            <div className={styles.optionList}>
                {poll.options.map(opt => (
                    <div
                        key={opt.optionId}
                        className={styles.optionItem}
                        onClick={() => setSelectedOptionId(opt.optionId)}
                    >
                        <div className={`${styles.radio} ${selectedOptionId === opt.optionId ? styles.radioSelected : ''}`}>
                            {selectedOptionId === opt.optionId && <div className={styles.radioInner} />}
                        </div>
                        <span className={styles.optionText}>{opt.text}</span>
                    </div>
                ))}
            </div>

            <div className={styles.footerRow}>
                {isCreator && hasAnyVote && (
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClosePoll}
                        disabled={closing}
                    >
                        {closing ? '종료 중...' : '투표 종료'}
                    </button>
                )}
                <button
                    className={`${styles.voteButton} ${selectedOptionId ? styles.voteButtonReady : ''}`}
                    onClick={handleVote}
                    disabled={!selectedOptionId}
                >
                    투표하기
                </button>
            </div>
        </div>
    );
};

export default PollBubble;
